import React, { useState, useEffect } from 'react';
import '../../styles/Payroll.css';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  LinearProgress,
  Divider,
  IconButton,
  FormHelperText,
  Tabs,
  Tab
} from '@mui/material';
import {
  Close as CloseIcon,
  Save as SaveIcon,
  Person as PersonIcon,
  Work as WorkIcon,
  AccountBalance as BankIcon
} from '@mui/icons-material';
import { EmployeeService } from '../../services/payrollService';
import { Employee, EmployeeStatus, JobTitle } from '../../types/payroll';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index}>
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

// Type for the nested employee structure that comes from the API
type EmployeeWithProfile = {
  employee: Employee;
  salaryProfiles: any[];
} | Employee;

interface EmployeeDialogProps {
  open: boolean;
  employee?: EmployeeWithProfile | null;
  onClose: () => void;
  onSave: (employee: Employee) => void;
}

const EmployeeDialog: React.FC<EmployeeDialogProps> = ({
  open,
  employee,
  onClose,
  onSave
}) => {
  console.log('=== EmployeeDialog RENDER ===');
  console.log('Props received:', { open, employee, onClose: !!onClose, onSave: !!onSave });
  
  // Helper function to extract employee data from nested structure
  const getEmployeeData = (emp: any): Employee | null => {
    if (!emp) return null;
    // Handle nested structure: {employee: {...}, salaryProfiles: [...]}
    return emp.employee || emp;
  };
  
  // Initialize form data directly from employee prop if available
  const getInitialFormData = () => {
    console.log('getInitialFormData called with employee:', employee);
    
    const employeeData = getEmployeeData(employee);
    console.log('Extracted employee data:', employeeData);
    console.log('Available fields in employee data:', employeeData ? Object.keys(employeeData) : 'null');
    console.log('Name fields specifically:', {
      // Check all possible field names
      first_name: (employeeData as any)?.first_name,
      middle_name: (employeeData as any)?.middle_name,
      last_name: (employeeData as any)?.last_name,
      // Legacy field names
      first_name_en: employeeData?.first_name_en,
      last_name_en: employeeData?.last_name_en,
      first_name_vi: employeeData?.first_name_vi,
      last_name_vi: employeeData?.last_name_vi,
      full_name: employeeData?.full_name
    });
    
    // Parse bank_info if it exists
    let bankAccountNo = '';
    let bankName = '';
    let accountName = '';
    
    if (employeeData?.bank_info) {
      try {
        // Handle both string and object formats
        const bankInfo = typeof employeeData.bank_info === 'string' 
          ? JSON.parse(employeeData.bank_info) 
          : employeeData.bank_info;
          
        bankAccountNo = bankInfo.account_no || '';
        bankName = bankInfo.bank_name || '';
        accountName = bankInfo.account_name || '';
        
        console.log('Parsed bank info:', { bankAccountNo, bankName, accountName });
      } catch (e) {
        console.error('Error parsing bank_info:', e);
      }
    } else {
      // Fallback to legacy fields if they exist
      bankAccountNo = employeeData?.bank_account_no || '';
      bankName = employeeData?.bank_name || '';
    }
    
    if (employeeData) {
      return {
        id: employeeData.id,
        employee_no: employeeData.employee_no || '',
        // Use database field names
        first_name: (employeeData as any).first_name || employeeData.first_name_en || '',
        middle_name: (employeeData as any).middle_name || '',
        last_name: (employeeData as any).last_name || employeeData.last_name_en || '',
        // Legacy field names
        first_name_en: employeeData.first_name_en || (employeeData as any).first_name || '',
        last_name_en: employeeData.last_name_en || (employeeData as any).last_name || '',
        first_name_vi: employeeData.first_name_vi || '',
        last_name_vi: employeeData.last_name_vi || '',
        full_name: employeeData.full_name || '',
        job_title_id: employeeData.job_title_id || 0,
        job_title: employeeData.job_title || '',
        status: employeeData.status || 'active',
        phone: employeeData.phone || '',
        email: employeeData.email || '',
        tax_code: employeeData.tax_code || '',
        si_code: employeeData.si_code || '',
        national_id: employeeData.national_id || '',
        dependants: employeeData.dependants || 0,
        bank_account_no: bankAccountNo,
        bank_name: bankName,
        account_name: accountName
      };
    }
    return {
      employee_no: '',
      first_name: '',
      middle_name: '',
      last_name: '',
      full_name: '',
      job_title_id: undefined,
      status: 'active' as EmployeeStatus,
      email: '',
      phone: '',
      tax_code: '',
      si_code: '',
      national_id: '',
      dependants: 0,
      bank_account_no: '',
      bank_name: '',
      account_name: ''
    };
  };

  const initialFormData = getInitialFormData();
  console.log('Initial form data calculated:', initialFormData);
  
  const [formData, setFormData] = useState<Partial<Employee>>(initialFormData);

  const [jobTitles, setJobTitles] = useState<JobTitle[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [activeTab, setActiveTab] = useState(0);

  const isEditMode = !!getEmployeeData(employee);
  console.log('isEditMode:', isEditMode);

  // Load job titles when dialog opens
  useEffect(() => {
    const loadJobTitles = async () => {
      if (!open) return;
      
      try {
        setLoading(true);
        const jobTitlesResponse = await EmployeeService.getJobTitles();
        setJobTitles(jobTitlesResponse.data || []);
      } catch (error: any) {
        setError(error.response?.data?.message || 'Failed to load job titles');
      } finally {
        setLoading(false);
      }
    };

    loadJobTitles();
  }, [open]);

  // Reset form data whenever employee prop changes
  useEffect(() => {
    console.log('=== useEffect: Employee prop changed ===');
    console.log('Raw employee prop:', employee);
    
    // Handle nested employee structure
    const employeeData = getEmployeeData(employee);
    console.log('Extracted employee data:', employeeData);
    console.log('Employee details:', {
      id: employeeData?.id,
      employee_no: employeeData?.employee_no,
      first_name_en: employeeData?.first_name_en,
      last_name_en: employeeData?.last_name_en,
      first_name_vi: employeeData?.first_name_vi,
      last_name_vi: employeeData?.last_name_vi,
      full_name: employeeData?.full_name,
      job_title_id: employeeData?.job_title_id,
      status: employeeData?.status
    });
    
    const newFormData = getInitialFormData();
    console.log('New form data calculated:', newFormData);
    console.log('Setting form data...');
    setFormData(newFormData);
    console.log('Form data set complete');
  }, [employee]);

  // Log current form data for debugging
  useEffect(() => {
    console.log('=== useEffect: Form data changed ===');
    console.log('Current form data state:', formData);
    console.log('Form data details:', {
      employee_no: formData.employee_no,
      first_name_en: formData.first_name_en,
      last_name_en: formData.last_name_en,
      first_name_vi: formData.first_name_vi,
      last_name_vi: formData.last_name_vi,
      full_name: formData.full_name,
      job_title_id: formData.job_title_id,
      status: formData.status
    });
  }, [formData]);

  // Reset everything when dialog closes or opens for new employee
  useEffect(() => {
    if (!open) {
      // Clear errors when dialog closes
      setFormErrors({});
      setError(null);
      setActiveTab(0);
    } else if (open && !employee) {
      // Reset form to initial state when opening for new employee
      setFormData({
        employee_no: '',
        first_name: '',
        middle_name: '',
        last_name: '',
        full_name: '',
        job_title_id: undefined,
        status: 'active' as EmployeeStatus,
        email: '',
        phone: '',
        tax_code: '',
        si_code: '',
        national_id: '',
        dependants: 0,
        bank_account_no: '',
        bank_name: '',
        account_name: ''
      });
      setFormErrors({});
      setError(null);
      setActiveTab(0);
    }
  }, [open, employee]);

  const handleChange = (field: keyof Employee, value: any) => {
    console.log(`Updating field ${field} with value:`, value);
    
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };
      
      // Auto-generate full name when name fields change
      if (field === 'first_name' || field === 'middle_name' || field === 'last_name') {
        const firstName = field === 'first_name' ? value : prev.first_name;
        const middleName = field === 'middle_name' ? value : prev.middle_name;
        const lastName = field === 'last_name' ? value : prev.last_name;
        
        // Generate full name from parts
        const nameParts = [firstName, middleName, lastName].filter(part => part && part.trim());
        updated.full_name = nameParts.join(' ');
      }
      
      console.log('Updated form data:', updated);
      return updated;
    });

    // Clear field error
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};
    
    // Only validate required fields
    if (!formData.employee_no?.trim()) {
      errors.employee_no = 'Employee number is required';
    }
    
    if (!formData.first_name?.trim()) {
      errors.first_name = 'First name is required';
    }
    
    if (!formData.last_name?.trim()) {
      errors.last_name = 'Last name is required';
    }

    // Optional field validations
    if (formData.email && !/^\S+@\S+\.\S+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (formData.dependants !== undefined && formData.dependants < 0) {
      errors.dependants = 'Dependants cannot be negative';
    }

    console.log('Validation errors:', errors);
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;
    
    try {
      setSaving(true);
      setError(null);
      
      // Prepare employee data for API
      const employeeData: any = {
        employee_no: formData.employee_no,
        first_name: formData.first_name,
        middle_name: formData.middle_name,
        last_name: formData.last_name,
        full_name: formData.full_name,
        status: formData.status || 'active',
        // Only include optional fields if they have values
        ...(formData.email && { email: formData.email }),
        ...(formData.phone && { phone: formData.phone }),
        ...(formData.job_title_id && { job_title_id: formData.job_title_id }),
        ...(formData.tax_code && { tax_code: formData.tax_code }),
        ...(formData.si_code && { si_code: formData.si_code }),
        ...(formData.national_id && { national_id: formData.national_id }),
        dependants: formData.dependants || 0,
        // Convert bank info to object format (backend will handle JSON conversion)
        bank_info: {
          account_no: formData.bank_account_no || '',
          bank_name: formData.bank_name || '',
          account_name: formData.account_name || ''
        }
      };
      
      console.log('Saving employee data:', employeeData);
      
      // Call API to save employee
      let response;
      const employeeData_extracted = getEmployeeData(employee);
      if (isEditMode && employeeData_extracted?.id) {
        console.log('Updating existing employee with ID:', employeeData_extracted.id);
        response = await EmployeeService.updateEmployee(employeeData_extracted.id, employeeData);
      } else {
        console.log('Creating new employee');
        response = await EmployeeService.createEmployee(employeeData);
      }

      console.log('API response:', response);

      if (response && response.data) {
        console.log('Employee saved successfully:', response.data);
        onSave(response.data);
        onClose();
      } else {
        const errorMsg = response?.message || 'Failed to save employee';
        console.error('Save failed:', errorMsg);
        setError(errorMsg);
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.message || error.message || 'Failed to save employee';
      console.error('Error saving employee:', error);
      console.error('Error details:', error.response?.data);
      
      // Show detailed validation errors if available
      if (error.response?.data?.errors) {
        const errorList = error.response.data.errors.map((e: any) => `${e.param}: ${e.msg}`).join(', ');
        setError(`Validation failed: ${errorList}`);
      } else {
        setError(errorMsg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth className="payroll-dialog">
      <DialogTitle className="payroll-card-header">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" className="payroll-card-title">
            {isEditMode ? 'Edit Employee' : 'Add New Employee'}
          </Typography>
          <IconButton onClick={onClose} size="small" className="payroll-action-button">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers className="payroll-card-body">
        {loading && <LinearProgress />}
        
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} className="payroll-notification-error">
            {error}
          </Alert>
        )}

        <Tabs value={activeTab} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tab icon={<PersonIcon />} label="Personal" className="payroll-tab" />
          <Tab icon={<WorkIcon />} label="Employment" className="payroll-tab" />
          <Tab icon={<BankIcon />} label="Banking & Tax" className="payroll-tab" />
        </Tabs>

        {/* Personal Information Tab */}
        <TabPanel value={activeTab} index={0}>
          <Box display="flex" flexDirection="column" gap={2} className="payroll-form-group">
            <Box display="flex" gap={2}>
              <TextField
                label="Employee Number"
                value={formData.employee_no || ''}
                onChange={(e) => handleChange('employee_no', e.target.value)}
                required
                error={!!formErrors.employee_no}
                helperText={formErrors.employee_no}
                sx={{ flex: 1 }}
              />
              <FormControl sx={{ flex: 1 }} error={!!formErrors.status}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={formData.status || 'active'}
                  onChange={(e) => handleChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                </Select>
                <FormHelperText>Defaults to Active</FormHelperText>
              </FormControl>
            </Box>

            <Box display="flex" gap={2}>
              <TextField
                label="First Name"
                value={formData.first_name || ''}
                onChange={(e) => handleChange('first_name', e.target.value)}
                required
                error={!!formErrors.first_name}
                helperText={formErrors.first_name}
                sx={{ flex: 1 }}
              />
              <TextField
                label="Middle Name"
                value={formData.middle_name || ''}
                onChange={(e) => handleChange('middle_name', e.target.value)}
                helperText="Optional"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Last Name"
                value={formData.last_name || ''}
                onChange={(e) => handleChange('last_name', e.target.value)}
                required
                error={!!formErrors.last_name}
                helperText={formErrors.last_name}
                sx={{ flex: 1 }}
              />
            </Box>

            <TextField
              label="Full Name"
              value={formData.full_name || ''}
              onChange={(e) => handleChange('full_name', e.target.value)}
              helperText="Auto-generated from name fields above. You can override if needed."
              fullWidth
            />

            <Box display="flex" gap={2}>
              <TextField
                label="Phone"
                value={formData.phone || ''}
                onChange={(e) => handleChange('phone', e.target.value)}
                helperText="Optional"
                sx={{ flex: 1 }}
              />
              <TextField
                label="Email"
                type="email"
                value={formData.email || ''}
                onChange={(e) => handleChange('email', e.target.value)}
                error={!!formErrors.email}
                helperText={formErrors.email || 'Optional'}
                sx={{ flex: 1 }}
              />
            </Box>
          </Box>
        </TabPanel>

        {/* Employment Tab */}
        <TabPanel value={activeTab} index={1}>
          <Box display="flex" flexDirection="column" gap={2} className="payroll-form-group">
            <FormControl error={!!formErrors.job_title_id}>
              <InputLabel>Job Title</InputLabel>
              <Select
                value={formData.job_title_id || ''}
                onChange={(e) => handleChange('job_title_id', e.target.value)}
                label="Job Title"
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {jobTitles.map((title) => (
                  <MenuItem key={title.id} value={title.id}>
                    {title.title_name_en} {title.title_name_vi && `(${title.title_name_vi})`}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {formErrors.job_title_id || 'Optional - Select employee job title'}
              </FormHelperText>
            </FormControl>

            <TextField
              label="Number of Dependants"
              type="number"
              value={formData.dependants || 0}
              onChange={(e) => handleChange('dependants', parseInt(e.target.value) || 0)}
              error={!!formErrors.dependants}
              helperText={formErrors.dependants || 'For tax calculation purposes (Optional)'}
              InputProps={{ inputProps: { min: 0 } }}
            />
          </Box>
        </TabPanel>

        {/* Banking & Tax Tab */}
        <TabPanel value={activeTab} index={2}>
          <Box display="flex" flexDirection="column" gap={3} className="payroll-form-group">
            <Box>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ mb: 2 }}>
                Tax Information
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" gap={2}>
                  <TextField
                    label="Tax Code"
                    value={formData.tax_code || ''}
                    onChange={(e) => handleChange('tax_code', e.target.value)}
                    helperText="Personal Income Tax code (Optional)"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Social Insurance Code"
                    value={formData.si_code || ''}
                    onChange={(e) => handleChange('si_code', e.target.value)}
                    helperText="Optional"
                    sx={{ flex: 1 }}
                  />
                </Box>
                <TextField
                  label="National ID"
                  value={formData.national_id || ''}
                  onChange={(e) => handleChange('national_id', e.target.value)}
                  helperText="CMND/CCCD number (Optional)"
                  fullWidth
                />
              </Box>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle1" fontWeight="medium" gutterBottom sx={{ mb: 2 }}>
                Banking Information
              </Typography>
              <Box display="flex" flexDirection="column" gap={2}>
                <Box display="flex" gap={2}>
                  <TextField
                    label="Bank Account Number"
                    value={formData.bank_account_no || ''}
                    onChange={(e) => handleChange('bank_account_no', e.target.value)}
                    helperText="Optional"
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    label="Bank Name"
                    value={formData.bank_name || ''}
                    onChange={(e) => handleChange('bank_name', e.target.value)}
                    helperText="Optional"
                    sx={{ flex: 1 }}
                  />
                </Box>
                <TextField
                  label="Account Name"
                  value={formData.account_name || ''}
                  onChange={(e) => handleChange('account_name', e.target.value)}
                  helperText="Name of the account holder (Optional)"
                  fullWidth
                />
              </Box>
            </Box>
          </Box>
        </TabPanel>
      </DialogContent>

      <DialogActions className="payroll-dialog-actions">
        <Button 
          onClick={onClose} 
          disabled={saving}
          className="payroll-button payroll-button-secondary"
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={saving}
          startIcon={<SaveIcon />}
          className="payroll-button payroll-button-primary"
        >
          {saving ? 'Saving...' : (isEditMode ? 'Update' : 'Create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmployeeDialog;
